"use client";

/**
 * AgentFinalCTA — cube-card CTA for /agents/[slug] pages.
 *
 * Client island: owns the CTABackgroundCubes WebGL canvas + mouse handlers.
 * The H2, sub, and buttons are plain HTML — server-renderable for SEO.
 * Matches landing CTA.tsx pattern exactly; no framer-motion.
 *
 * Per DESIGN_SPEC.md §1.6 + §9 errata bullet 4.
 */

import { useCallback, useRef } from "react";
import dynamic from "next/dynamic";
import { ctaMouseRef } from "@/components/landing/CTABackgroundCubes";

const CTABackgroundCubes = dynamic(
  () => import("@/components/landing/CTABackgroundCubes"),
  { ssr: false }
);

interface Props {
  title: string;
  sub: string;
  primaryHref: string;
  primaryLabel: string;
  secondaryHref?: string;
  secondaryLabel?: string;
  telegramLabel?: string;
}

export default function AgentFinalCTA({
  title,
  sub,
  primaryHref,
  primaryLabel,
  secondaryHref = "#developers",
  secondaryLabel = "List your agent",
  telegramLabel,
}: Props) {
  const invalidateFnRef = useRef<(() => void) | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      ctaMouseRef.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      };
      invalidateFnRef.current?.();
    },
    []
  );

  const handleMouseLeave = useCallback(() => {
    ctaMouseRef.current = null;
    invalidateFnRef.current?.();
  }, []);

  return (
    <section
      className="flex flex-col"
      style={{
        background: "#171717",
        paddingTop: 60,
        paddingBottom: 30,
        position: "relative",
      }}
    >
      {/* Violet cube card — full width minus gutters */}
      <div
        className="relative flex-1 cta-card cta-card-inner"
        style={{
          borderRadius: 20,
          overflow: "hidden",
          background: "#8635F6",
          minHeight: "66.5vh",
          margin: "0 auto",
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* WebGL cube background — client-only */}
        <CTABackgroundCubes invalidateFnRef={invalidateFnRef} />

        {/* Content centered over cubes */}
        <div
          className="absolute inset-0 z-10 px-6 text-center flex flex-col items-center justify-center"
        >
          <h2
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2rem,4.5vw,56px)",
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              color: "#F2F0ED",
              margin: "0 0 20px",
              textWrap: "balance",
            }}
          >
            {title}
          </h2>

          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1.5,
              color: "rgba(242,240,237,0.9)",
              maxWidth: "90vw",
              margin: "0 0 32px",
            }}
          >
            {sub}
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              padding: "0 16px",
            }}
          >
            {/* Primary: cream pill, dark text */}
            <a
              href={primaryHref}
              style={{
                fontFamily: "var(--font-body)",
                display: "inline-flex",
                alignItems: "center",
                height: 50,
                padding: "0 32px",
                borderRadius: 9999,
                background: "#F2F0ED",
                color: "#171717",
                fontSize: 15,
                fontWeight: 700,
                textDecoration: "none",
                transition: "box-shadow 150ms ease-out",
                whiteSpace: "nowrap",
              }}
              className="cta-primary-btn"
            >
              {primaryLabel}
            </a>

            {/* Secondary: transparent outline, cream text */}
            {secondaryHref && secondaryLabel && (
              <a
                href={secondaryHref}
                style={{
                  fontFamily: "var(--font-body)",
                  display: "inline-flex",
                  alignItems: "center",
                  height: 50,
                  padding: "0 32px",
                  borderRadius: 9999,
                  border: "2px solid rgba(242,240,237,0.4)",
                  background: "rgba(134,53,246,0.25)",
                  color: "#F2F0ED",
                  fontSize: 15,
                  fontWeight: 700,
                  textDecoration: "none",
                  transition: "background 150ms ease-out",
                  whiteSpace: "nowrap",
                }}
                className="cta-secondary-btn"
              >
                {secondaryLabel}
              </a>
            )}
          </div>

          {/* Tertiary Telegram link */}
          {telegramLabel && (
            <div style={{ marginTop: 16 }}>
              <a
                href="https://t.me/BotworkAgent_bot"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 14,
                  color: "rgba(242,240,237,0.55)",
                  textDecoration: "none",
                  transition: "color 150ms",
                }}
                className="cta-telegram-link"
              >
                {telegramLabel}
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
